import React, { useState, useEffect } from 'react';
import { observer } from "mobx-react";
import {
	AgentInputWrapper,
	AgentWidgetHeader,
	AgentWidgetContainer,
	AgentWidgetContent,
	SelectedAgentHeader,
	SelectedAgentWrapper,
	BackButton,
	SelectedAgentName,
	AgentInfo,
	AgentAvatar,
	WidgetActions,
	TabsWrapper,
	AgentActionsWrapper
} from './AgentsWidget.styled';
import { Icon } from "../../../elements";
import AgentList from '../../../elements/v2/AgentList/AgentList';
import Tabs2 from '../../../elements/v2/Tabs2/Tabs2';
import SearchInput from './SearchInput';
import { Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChatTab from "./ChatTab";
import HistoryTab from "./HistoryTab"
import { useNavigate } from 'react-router-dom';
import { useStore } from "../../../hooks";
import { AgentsWidgetTabs } from '../../../utils/enums';

const AgentsWidget = observer(({ onClose, isCollapsed }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedAgent, setSelectedAgent] = useState(null);
	const [activeTab, setActiveTab] = useState(AgentsWidgetTabs.CHAT);
	const navigate = useNavigate();
	const { authStore, agentsWidgetHistoryStore, historyAgentStore } = useStore();

	useEffect(() => {
		const loadConversations = async () => {
			if (selectedAgent?.id) {
				await agentsWidgetHistoryStore.fetchConversations(
					selectedAgent.id,
					authStore.userId,
				);
			}
		};

		loadConversations();
	}, [selectedAgent?.id, authStore.userId, agentsWidgetHistoryStore]);

	if (isCollapsed) {
		return null;
	}

	const handleAgentClick = (agent) => {
		setSelectedAgent(agent);
		setActiveTab(AgentsWidgetTabs.CHAT);
		agentsWidgetHistoryStore.clearMessagesData();
		agentsWidgetHistoryStore.setLastConversationID("");
		agentsWidgetHistoryStore.setSelectedConversationId(null);
	};

	const handleBackClick = () => {
		setSelectedAgent(null);
		agentsWidgetHistoryStore.clearMessagesData();
		agentsWidgetHistoryStore.setLastConversationID("");
		agentsWidgetHistoryStore.setSelectedConversationId(null);
	};

	const handleTabChange = (index) => {
		setActiveTab(index);
		if (index === AgentsWidgetTabs.HISTORY && selectedAgent?.id) {
			agentsWidgetHistoryStore.fetchConversations(
				selectedAgent.id,
				authStore.userId,
			);
		}
	};

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	const navigateToHome = (agentId = '') => {
		const url = agentId ? `/home?agent=${agentId}` : '/home';
		navigate(url);
		onClose();
	};

	const cleanUpAgentsWidgetStore = () => {
		agentsWidgetHistoryStore.clearMessagesData();
		agentsWidgetHistoryStore.setLastConversationID("");
		agentsWidgetHistoryStore.setSelectedConversationId(null);
	};

	const transferConversationToHomePage = (conversationId, messagesToTransfer, agent) => {
		historyAgentStore.setIsLoadingConversation(true);
		historyAgentStore.setLastConversationID(conversationId);
		historyAgentStore.setSelectedConversationId(conversationId);
		
		if (agentsWidgetHistoryStore.conversations.length > 0) {
			const conversation = agentsWidgetHistoryStore.conversations.find(c => c.id === conversationId);
			if (conversation) {
				historyAgentStore.addConversation({
					id: conversation.id,
					title: conversation.title,
					last_modified: conversation.last_modified,
					agent_id: agent.id
				});
			}
		}

		historyAgentStore.clearMessagesData();
		messagesToTransfer.forEach(message => {
			historyAgentStore.messagesData.push({...message});
		});

		historyAgentStore.setShouldRefreshMessages(true);

		navigate(`/home?agent=${agent.id}`);

		cleanUpAgentsWidgetStore();
		onClose();
	};

	const handleNavigateToHome = async () => {
		if (!selectedAgent) {
			navigateToHome();
			return;
		}
		
		const hasActiveConversation = agentsWidgetHistoryStore.lastConversationID && 
			agentsWidgetHistoryStore.messagesData.length > 0;
		
		if (hasActiveConversation) {
			const conversationId = agentsWidgetHistoryStore.lastConversationID;
			
			const conversationBelongsToSelectedAgent = agentsWidgetHistoryStore.conversations.some(
				conv => conv.id === conversationId && conv.agent_id === selectedAgent.id
			);
			
			if (conversationBelongsToSelectedAgent) {
				const messagesToTransfer = [...agentsWidgetHistoryStore.messagesData];

				navigate(`/home?agent=${selectedAgent.id}`);

				setTimeout(() => {
					transferConversationToHomePage(conversationId, messagesToTransfer, selectedAgent);
				}, 1000);
			} else {
				navigateToHome(selectedAgent.id);
			}
		} else {
			navigateToHome(selectedAgent.id);
		}
	};

	const tabs = [
		{ label: 'Chat', disabled: false },
		{ label: 'History', disabled: false }
	];

	return (
		<AgentWidgetContainer className="agent-list-widget">
			{!selectedAgent ? (
				<AgentWidgetHeader>
					<AgentActionsWrapper>
						<div onClick={handleNavigateToHome}>
							<Icon name="redirect" height="16px" width="16px" />
						</div>
						<div onClick={onClose}>
							<Icon name="closeLight" height="16px" width="16px" />
						</div>
					</AgentActionsWrapper>
					<AgentInputWrapper>
						<SearchInput
							value={searchTerm}
							onChange={handleSearchChange}
							placeholder="Search"
							iconHeight="auto"
							iconWidth="20px"
							height="36px"
						/>
					</AgentInputWrapper>
				</AgentWidgetHeader>
			) : (
				<SelectedAgentHeader>
					<SelectedAgentWrapper>
						<BackButton onClick={handleBackClick}>
							<ChevronLeftIcon />
						</BackButton>
						<AgentInfo>
							<AgentAvatar>
								{selectedAgent?.photo_url ? (
									<Avatar
										src={selectedAgent.photo_url}
										alt={selectedAgent.name}
										sx={{ width: 32, height: 32 }}
									/>
								) : (
									<Icon
										name="carbonUserAvatarFilled"
										height="32px"
										width="32px"
									/>
								)}
							</AgentAvatar>
							<SelectedAgentName>{selectedAgent?.name}</SelectedAgentName>
						</AgentInfo>
					</SelectedAgentWrapper>
					<WidgetActions>
						<div onClick={handleNavigateToHome}>
							<Icon name="redirect" height="16px" width="16px" />
						</div>
						<div onClick={onClose}>
							<Icon name="closeLight" height="16px" width="16px" />
						</div>
					</WidgetActions>
				</SelectedAgentHeader>
			)}
			
			<AgentWidgetContent id="agent-widget-content">
				{!selectedAgent ? (
					<>
						<AgentList
							searchTerm={searchTerm}
							onAgentClick={handleAgentClick}
							showDetails={true}
							isExpanded={true}
						/>
					</>
				) : (
					<TabsWrapper>
						<Tabs2 
							tabs={tabs} 
							onToggleTab={handleTabChange}
							defaultTab={activeTab}
						/>

						<Tabs2.Outlet show={activeTab === AgentsWidgetTabs.CHAT}>
								<ChatTab selectedAgent={selectedAgent} />
						</Tabs2.Outlet>

						<Tabs2.Outlet show={activeTab === AgentsWidgetTabs.HISTORY}>
							<HistoryTab selectedAgent={selectedAgent} onTabChange={handleTabChange}/>
						</Tabs2.Outlet>
					</TabsWrapper>
				)}
			</AgentWidgetContent>
		</AgentWidgetContainer>
	);
});

export default AgentsWidget;
